import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  UsersIcon, 
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, cn } from '../utils';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green  
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280', // Gray
];

const GroupFormModal = ({ isOpen, onClose, group, mechanics, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: COLORS[0],
    members: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        description: group.description || '',
        color: group.color || COLORS[0],
        members: group.members?.map(member => member._id) || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: COLORS[0],
        members: []
      });
    }
  }, [group]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (group) {
        // Update existing group
        response = await apiService.updateGroup(group._id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          color: formData.color
        });

        // Handle member changes
        const currentMemberIds = group.members.map(m => m._id);
        const membersToAdd = formData.members.filter(id => !currentMemberIds.includes(id));
        const membersToRemove = currentMemberIds.filter(id => !formData.members.includes(id));

        // Add new members
        if (membersToAdd.length > 0) {
          await apiService.addGroupMembers(group._id, membersToAdd);
        }

        // Remove members
        for (const memberId of membersToRemove) {
          await apiService.removeGroupMember(group._id, memberId);
        }
      } else {
        // Create new group
        response = await apiService.createGroup({
          name: formData.name.trim(),
          description: formData.description.trim(),
          color: formData.color,
          members: formData.members
        });
      }

      if (response.success) {
        toast.success(`Group ${group ? 'updated' : 'created'} successfully`);
        onSave();
        onClose();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberToggle = (memberId) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.includes(memberId)
        ? prev.members.filter(id => id !== memberId)
        : [...prev.members, memberId]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">
            {group ? 'Edit Group' : 'Create New Group'}
          </h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Group Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="form-input w-full"
              placeholder="Enter group name..."
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="form-input w-full h-24 resize-none"
              placeholder="Enter group description..."
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Group Color
            </label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, color }))}
                  className={cn(
                    'w-8 h-8 rounded-full border-2',
                    formData.color === color ? 'border-white' : 'border-transparent'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Members */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Members ({formData.members.length} selected)
            </label>
            <div className="max-h-48 overflow-y-auto border border-dark-600 rounded-lg">
              {mechanics.map((mechanic) => (
                <label
                  key={mechanic._id}
                  className="flex items-center p-3 hover:bg-dark-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.members.includes(mechanic._id)}
                    onChange={() => handleMemberToggle(mechanic._id)}
                    className="form-checkbox"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-white">{mechanic.name}</div>
                    <div className="text-sm text-dark-400">{mechanic.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? (
                <div className="spinner"></div>
              ) : (
                group ? 'Update Group' : 'Create Group'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchGroups();
    fetchMechanics();
  }, [filters, pagination]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await apiService.getGroups({ ...filters, ...pagination });
      if (response.success && response.groups) {
        setGroups(response.groups);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchMechanics = async () => {
    try {
      const response = await apiService.getMechanics();
      if (response.success && response.mechanics) {
        setMechanics(response.mechanics);
      }
    } catch (error) {
      console.error('Failed to fetch mechanics:', error);
    }
  };

  const handleSearch = (search) => {
    setFilters(prev => ({
      ...prev,
      search: search || undefined
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDeleteGroup = async (group) => {
    if (window.confirm(`Are you sure you want to delete "${group.name}"? This action cannot be undone.`)) {
      try {
        const response = await apiService.deleteGroup(group._id);
        if (response.success) {
          toast.success('Group deleted successfully');
          fetchGroups();
        }
      } catch (error) {
        toast.error(error.message || 'Failed to delete group');
      }
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowModal(true);
  };

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGroup(null);
  };

  const handleSaveGroup = () => {
    fetchGroups();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Mechanic Groups</h1>
        <button
          onClick={handleCreateGroup}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Group
        </button>
      </div>

      {/* Filters */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
              <input
                type="text"
                placeholder="Search groups..."
                className="form-input pl-10"
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-dark-800 border border-dark-700 rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-dark-600 rounded mb-2"></div>
              <div className="h-3 bg-dark-600 rounded w-2/3 mb-4"></div>
              <div className="h-2 bg-dark-600 rounded w-1/2"></div>
            </div>
          ))
        ) : groups.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <UserGroupIcon className="h-16 w-16 text-dark-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Groups Found</h3>
            <p className="text-dark-400 mb-6">Create your first mechanic group to get started.</p>
            <button
              onClick={handleCreateGroup}
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create First Group
            </button>
          </div>
        ) : (
          groups.map((group) => (
            <div
              key={group._id}
              className="bg-dark-800 border border-dark-700 rounded-lg p-6 hover:border-dark-600 transition-colors"
              style={{ borderLeftColor: group.color, borderLeftWidth: '4px' }}
            >
              {/* Group Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: group.color }}
                  ></div>
                  <h3 className="text-lg font-semibold text-white truncate">
                    {group.name}
                  </h3>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleEditGroup(group)}
                    className="text-dark-400 hover:text-primary-400 p-1"
                    title="Edit group"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(group)}
                    className="text-dark-400 hover:text-red-400 p-1"
                    title="Delete group"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Description */}
              {group.description && (
                <p className="text-dark-300 text-sm mb-4 line-clamp-2">
                  {group.description}
                </p>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-dark-400 mb-4">
                <div className="flex items-center">
                  <UsersIcon className="h-4 w-4 mr-1" />
                  <span>{group.memberCount || group.members?.length || 0} members</span>
                </div>
                <span>
                  Created {formatDistanceToNow(group.createdAt)}
                </span>
              </div>

              {/* Members Preview */}
              <div className="mb-4">
                <div className="text-xs text-dark-400 uppercase tracking-wide mb-2">Members</div>
                <div className="flex flex-wrap gap-1">
                  {group.members?.length > 0 ? (
                    group.members.slice(0, 4).map((member) => (
                      <span
                        key={member._id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-dark-700 text-dark-300"
                      >
                        {member.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-dark-500">No members assigned</span>
                  )}
                  {group.members?.length > 4 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-dark-700 text-dark-300">
                      +{group.members.length - 4} more
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Link
                  to={`/groups/${group._id}/tasks`}
                  className="flex-1 btn btn-secondary text-center"
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Tasks
                </Link>
                <button
                  onClick={() => handleEditGroup(group)}
                  className="flex-1 btn btn-primary"
                >
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Manage
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-dark-300">
            Showing page {pagination.page} of {totalPages}
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="btn btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
              disabled={pagination.page === totalPages}
              className="btn btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Group Form Modal */}
      <GroupFormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        group={editingGroup}
        mechanics={mechanics}
        onSave={handleSaveGroup}
      />
    </div>
  );
};

export default Groups;